import React from 'react';
import {
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

import {
  calendarViewOptions,
  TCalendarViewOptions,
} from '../../calendarHelper';

interface CalendarHeaderToolBarProps {
  title: string;
  view: TCalendarViewOptions;
  onToday: () => void;
  onNextDate: () => void;
  onPrevDate: () => void;
  onChangeView: (viewOption: TCalendarViewOptions) => void;
}

const RootStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 0',
  [theme.breakpoints.only('xs')]: {
    justifyContent: 'center',
  },
}));

const CalendarHeaderToolBar: React.FC<CalendarHeaderToolBarProps> = ({
  title,
  view,
  onToday,
  onNextDate,
  onPrevDate,
  onChangeView,
}) => {
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  // State
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const closeMenu = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <RootStyle>
        {!isXsScreen ? (
          <Button
            id="view-options-btn"
            variant="text"
            color="inherit"
            aria-controls={open ? 'view-options-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={openMenu}
            startIcon={<view.icon />}
            endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            sx={{ minWidth: '120px' }}
          >
            {view.label}
          </Button>
        ) : null}
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton onClick={onPrevDate}>
            <ChevronLeftIcon sx={{ width: 30, height: 30 }} />
          </IconButton>

          <Typography variant="h6" fontWeight={600} textAlign="center">
            {title}
          </Typography>

          <IconButton onClick={onNextDate}>
            <ChevronRightIcon sx={{ width: 30, height: 30 }} />
          </IconButton>
        </Stack>
        {!isXsScreen ? (
          <Stack>
            <Button
              disableElevation
              variant="contained"
              color="inherit"
              onClick={onToday}
              sx={{
                fontWeight: 600,
                fontSize: '14px',
                backgroundColor: '#E2E8F0',
              }}
            >
              Today
            </Button>
          </Stack>
        ) : null}
      </RootStyle>
      <Menu
        id="view-options-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        MenuListProps={{
          'aria-labelledby': 'view-options-btn',
        }}
        PaperProps={{
          sx: {
            p: '8px',
            borderRadius: '12px',
          },
        }}
      >
        {calendarViewOptions.map((item, index) => {
          const _handleClick = () => {
            onChangeView(item);
            closeMenu();
          };

          const LiIcon = item.icon;
          const selected = item.value === view.value;

          return (
            <MenuItem
              key={`li-${index}`}
              selected={selected}
              onClick={_handleClick}
            >
              <ListItemIcon>
                <LiIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{item.label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default CalendarHeaderToolBar;
